import { useCallback, useEffect, useMemo, useState } from "react";
import { Bookmark, FileText, Info, RotateCcw, Shield } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { AC } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import {
  cloneGrants,
  grantsEqual,
  PERMISSION_GROUPS,
  PERMISSION_GUIDE_HREF,
  permissionTestId,
  preferredGrants,
  setGrant,
  USER_CLASSES,
} from "@/lib/userPermissionsCatalog";
import { getOrgUserPermissions, saveOrgUserPermissions } from "@/lib/userPermissionsApi";

function PermissionInfo({ label, description, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <span className={`ac-perm-info${open ? " is-open" : ""}`}>
      <button
        type="button"
        className="ac-perm-info-btn"
        aria-label={`About ${label}`}
        title={description}
        data-testid={testId}
        onClick={() => setOpen((current) => !current)}
        onBlur={() => setOpen(false)}
      >
        <Info aria-hidden="true" />
      </button>
      <span role="tooltip" className="ac-perm-tip">
        {description}
      </span>
    </span>
  );
}

export default function UserPermissionsGrid() {
  const { context } = useAuth();
  const organizationId = context?.organization?.id;
  const [grants, setGrants] = useState(() => preferredGrants());
  const [saved, setSaved] = useState(() => preferredGrants());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const dirty = useMemo(() => !grantsEqual(grants, saved), [grants, saved]);
  const usingPreferred = useMemo(
    () => grantsEqual(grants, preferredGrants()),
    [grants],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getOrgUserPermissions({ organizationId });
      const next = cloneGrants(data.grants);
      setGrants(next);
      setSaved(next);
    } catch {
      const fallback = preferredGrants();
      setGrants(fallback);
      setSaved(fallback);
      toast.error("Failed to load user permissions.");
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    load();
  }, [load]);

  const applyPreferred = () => {
    setGrants(preferredGrants());
  };

  const cancel = () => {
    setGrants(cloneGrants(saved));
  };

  const save = async () => {
    setSaving(true);
    try {
      const data = await saveOrgUserPermissions({ organizationId, grants });
      const next = cloneGrants(data.grants);
      setGrants(next);
      setSaved(next);
      toast.success("User permissions saved.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to save user permissions.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ac-perm" data-testid={AC.userPermissions}>
      <section className="card emr-catalog__section ac-perm-head">
        <div className="ac-perm-title-row">
          <div>
            <h2 className="ac-perm-title">User Permissions</h2>
            <p className="ac-perm-subtitle">
              Configure what each user class can do within the system.
            </p>
          </div>
        </div>
        <div className="ac-perm-toolbar">
          <Button
            type="button"
            variant="outline"
            data-testid={AC.preferredPreferences}
            onClick={applyPreferred}
            disabled={loading || saving}
            className="btn emr-catalog__btn emr-catalog__btn--ghost ac-perm-preferred"
          >
            <Bookmark className="emr-catalog__btn-icon" />
            Preferred Preferences
          </Button>
          <a
            className="ac-perm-guide"
            data-testid={AC.permissionGuide}
            href={PERMISSION_GUIDE_HREF}
            target="_blank"
            rel="noreferrer"
          >
            <FileText aria-hidden="true" />
            Permission Guide (PDF)
          </a>
        </div>
        {usingPreferred ? (
          <p className="ac-perm-preferred-note">Using the recommended EMRbyMD defaults.</p>
        ) : null}
      </section>

      {loading ? (
        <section className="card emr-catalog__section">
          <p className="ac-loading">Loading user permissions…</p>
        </section>
      ) : (
        PERMISSION_GROUPS.map((group) => (
          <section
            key={group.id}
            className="card emr-catalog__section ac-perm-group"
            data-testid={`ac-perm-group-${group.id}`}
          >
            <h2 className="ac-perm-group-title">
              <span className="ac-perm-num">{group.number}</span>
              {group.label}
            </h2>
            <div className="ac-perm-table-wrap">
              <table className="ac-perm-table">
                <thead>
                  <tr>
                    <th scope="col">Permission</th>
                    {USER_CLASSES.map((item) => (
                      <th key={item.id} scope="col">
                        {item.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {group.permissions.map((permission) => (
                    <tr
                      key={permission.id}
                      data-testid={`ac-perm-row-${permission.id.replace(/\./g, "-")}`}
                    >
                      <th scope="row">
                        <span className="ac-perm-label">
                          {permission.label}
                          <PermissionInfo
                            label={permission.label}
                            description={permission.description}
                            testId={`ac-perm-info-${permission.id.replace(/\./g, "-")}`}
                          />
                        </span>
                      </th>
                      {USER_CLASSES.map((item) => {
                        const checked = Boolean(grants[permission.id]?.[item.id]);
                        const inputId = permissionTestId(permission.id, item.id);
                        return (
                          <td key={item.id}>
                            <label className="ac-perm-check-wrap" htmlFor={inputId}>
                              <input
                                id={inputId}
                                type="checkbox"
                                className="browser-default ac-perm-check"
                                checked={checked}
                                disabled={saving}
                                data-testid={inputId}
                                aria-label={`${permission.label} — ${item.label}`}
                                onChange={(event) =>
                                  setGrants((current) =>
                                    setGrant(
                                      current,
                                      permission.id,
                                      item.id,
                                      event.target.checked,
                                    ),
                                  )
                                }
                              />
                            </label>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ))
      )}

      <section className="card emr-catalog__section">
        <footer className="ac-footer ac-perm-footer">
          <Button
            type="button"
            variant="outline"
            data-testid={AC.resetPermissions}
            disabled={loading || saving}
            onClick={applyPreferred}
            className="btn emr-catalog__btn emr-catalog__btn--ghost ac-reset"
          >
            <RotateCcw className="emr-catalog__btn-icon" />
            Reset to Default
          </Button>
          <div className="ac-perm-actions">
            <Button
              type="button"
              variant="outline"
              data-testid={AC.cancelPermissions}
              disabled={loading || saving || !dirty}
              onClick={cancel}
              className="btn emr-catalog__btn emr-catalog__btn--ghost"
            >
              Cancel
            </Button>
            <Button
              data-testid={AC.savePermissions}
              onClick={save}
              disabled={loading || saving || !dirty}
              className="btn emr-catalog__btn ac-save ac-save--default"
            >
              <Shield className="emr-catalog__btn-icon" />
              Save Changes
            </Button>
          </div>
        </footer>
      </section>
    </div>
  );
}
